import { subYears } from "date-fns"

import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"

import SuperChart, { type DateChartSeries } from "./SuperChart"
import LoadingOverlay from "../LoadingOverlay"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useMemo } from "react"
import { type CustomTooltipProps } from "ag-grid-react"
import HeaderTooltip from "../table/tooltips/HeaderTooltip"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { ChartType } from "~/constants/graphs"
import { useChartSeriesConfig } from "~/constants/colors"
import { dateSeries } from "~/utils/graphUtils"
import { formatName } from "~/utils/namingUtils"

type Props = {
  customerIds: string[]
  variantIds: string[]
}

export const CustomerSalesTooltip = (props: CustomTooltipProps) => {
  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }
  const variantIds = props.node?.group ? props.value.variantIds : [props.data.variantId]
  const customerIds = props.node?.group ? props.value.customerIds : [props.data.customerId]
  return (
    <div className="w-[600px] mr-6 z-10">
      <CustomerSalesGraph customerIds={customerIds} variantIds={variantIds} />
    </div>
  )
}

export function CustomerSalesGraph({ customerIds, variantIds }: Props) {
  const chartConfig = useChartSeriesConfig()
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const { data: rawSalesForCustomer, isLoading } = api.sales.rawSalesByCustomerIds.useQuery(
    { customerIds, variantIds, warehouseId },
    { enabled: !!customerIds.length },
  )

  const series: DateChartSeries = useMemo(
    () =>
      dateSeries(chartConfig.sales, {
        name: formatName("sale", true, true),
        type: "column",
        data:
          rawSalesForCustomer?.map((entry) => ({
            milliseconds: new Date(entry.date).getTime(),
            value: entry.value,
          })) ?? [],
      }),
    [rawSalesForCustomer, chartConfig],
  )

  if (isLoading)
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{formatName("sale", true, true)}</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingOverlay />
        </CardContent>
      </Card>
    )

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{formatName("sale", true, true)}</CardTitle>
      </CardHeader>
      <CardContent>
        <SuperChart
          type={ChartType.TimeSeries}
          data={[series]}
          dateRange={{ start: subYears(startOfTodayUTC(), 1), end: startOfTodayUTC() }}
        />
      </CardContent>
    </Card>
  )
}
